import { Component } from '@theme/component';
import { DialogOpenEvent } from '@theme/dialog';
import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * Stock per store for each variant of a style, keyed by SKU and then by store name.
 *
 * @typedef {Map<string, Map<string, number>>} StockBySku
 */

/**
 * The body of a "Find in store" drawer: a size grid over the stores that hold the picked size.
 *
 * The size buttons are server-rendered, one per variant, and carry the variant SKU. Store rows are
 * cloned from server-rendered templates, one per store the merchandiser described plus a plain one
 * for any other store the stock data names. Stock is fetched once per stock URL and kept for the
 * life of the element: when the shopper reaches for the drawer this sits in, or straight away when
 * it sits outside one.
 *
 * Picking a size here never changes the page's selected variant. The drawer only follows the page:
 * it opens on the size the shopper last chose there, or failing that the first size a store holds.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} stores - The list the store groups are rendered into.
 * @property {HTMLTemplateElement} groupTemplate - The markup of one group of stores.
 * @property {HTMLElement} announcement - The status line read out to assistive technology.
 *
 * @extends {Component<Refs>}
 */
class FindInStore extends Component {
  requiredRefs = ['stores', 'groupTemplate', 'announcement'];

  /** @type {{ url: string, stock: Promise<StockBySku>, loaded: boolean } | undefined} */
  #request;

  /** @type {string | undefined} */
  #selectedSku;

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();
    this.#selectedSku = this.dataset.selectedSku;
    this.#abortController = new AbortController();

    const { signal } = this.#abortController;
    const drawer = this.closest('dialog-component');

    this.closest('.shopify-section')?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal,
    });

    if (!drawer) {
      this.#render();
      return;
    }

    // Reaching for the drawer's trigger starts the request, so the drawer usually opens with its answer.
    drawer.addEventListener('pointerenter', this.#preload, { signal });
    drawer.addEventListener('focusin', this.#preload, { signal });
    drawer.addEventListener(DialogOpenEvent.eventName, this.#handleDrawerOpen, { signal });

    // The shopper can open the drawer before this element upgrades, and its open event never repeats.
    if (this.closest('dialog')?.open) this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Size button clicked: show the stores that hold that size.
   * @param {Event} event
   */
  selectSize(event) {
    const button = /** @type {HTMLElement} */ (event.target).closest('[data-sku]');
    if (!(button instanceof HTMLElement)) return;

    this.#selectedSku = button.dataset.sku;
    this.#render();
  }

  retry() {
    this.#render();
  }

  /** @returns {HTMLElement[]} The size buttons, one per variant. */
  get #sizeButtons() {
    return Array.from(this.querySelectorAll('.find-in-store__size'));
  }

  #preload = () => {
    this.#load().stock.catch(() => {});
  };

  #handleDrawerOpen = () => {
    this.#render();
  };

  /**
   * Follows the size picked on the page, so the drawer opens on it.
   * @param {VariantUpdateEvent} event
   */
  #handleVariantUpdate = (event) => {
    const sku = event.detail.resource?.sku;
    // Another product's picker can share the section; only this product's sizes move the selection.
    if (!sku || !this.#sizeButtons.some((button) => button.dataset.sku === sku)) return;

    this.#selectedSku = sku;
    if (this.#request?.loaded) this.#render();
  };

  /**
   * The request for the current stock URL, made once. A failed request is forgotten, so the next
   * call tries again.
   */
  #load() {
    const url = this.dataset.stockUrl ?? '';

    if (this.#request?.url !== url) {
      const request = { url, stock: fetchStock(url), loaded: false };
      this.#request = request;
      request.stock.then(
        () => {
          request.loaded = true;
        },
        () => {
          if (this.#request === request) this.#request = undefined;
        }
      );
    }

    return /** @type {NonNullable<typeof this.#request>} */ (this.#request);
  }

  /**
   * Draws the stores that hold the picked size, or the message that stands in for them.
   */
  async #render() {
    const request = this.#load();
    if (!request.loaded) this.#setState('loading');
    this.#markSelected();

    /** @type {StockBySku} */
    let stock;
    try {
      stock = await request.stock;
    } catch {
      this.#setState('error');
      return;
    }

    const buttons = this.#sizeButtons;
    for (const button of buttons) {
      button.dataset.inStore = String(storesHolding(stock, button.dataset.sku).length > 0);
    }

    if (!buttons.some((button) => button.dataset.sku === this.#selectedSku)) {
      const firstInStore = buttons.find((button) => button.dataset.inStore === 'true');
      this.#selectedSku = (firstInStore ?? buttons[0])?.dataset.sku;
    }

    this.#markSelected();

    const stores = storesHolding(stock, this.#selectedSku);
    if (stores.length === 0) {
      this.refs.stores.replaceChildren();
      this.#setState('empty');
      return;
    }

    this.refs.stores.replaceChildren(...this.#createGroups(stores));
    this.#setState('ready', stores.map(([name]) => name).join(', '));
  }

  /**
   * Shows the state's message or the store list, and reads the same thing out. Messages swapped by
   * visibility alone go unannounced, so the status line carries the text.
   * @param {'loading' | 'empty' | 'error' | 'ready'} state
   * @param {string} [announcement] - What to read out. Defaults to the state's message.
   */
  #setState(state, announcement) {
    this.dataset.state = state;

    const message = announcement ?? this.dataset[`${state}Message`] ?? '';
    if (this.refs.announcement.textContent !== message) this.refs.announcement.textContent = message;
  }

  #markSelected() {
    for (const button of this.#sizeButtons) {
      button.setAttribute('aria-pressed', String(button.dataset.sku === this.#selectedSku));
    }
  }

  /**
   * Groups the stores by state, both in alphabetical order, with stores of no known state last.
   * @param {[name: string, stockOnHand: number][]} stores - The stores that hold the picked size.
   * @returns {DocumentFragment[]} One group per state.
   */
  #createGroups(stores) {
    /** @type {Map<string, DocumentFragment[]>} */
    const rowsByState = new Map();

    for (const [name, stockOnHand] of stores.sort(([a], [b]) => a.localeCompare(b))) {
      const template = this.#findStoreTemplate(name);
      if (!template) continue;

      const state = template.dataset.storeState ?? '';
      const rows = rowsByState.get(state) ?? [];
      rows.push(this.#createStoreRow(template, name, stockOnHand));
      rowsByState.set(state, rows);
    }

    return Array.from(rowsByState.keys())
      .sort((a, b) => Number(a === '') - Number(b === '') || a.localeCompare(b))
      .map((state) => {
        const group = /** @type {DocumentFragment} */ (this.refs.groupTemplate.content.cloneNode(true));
        const title = group.querySelector('.find-in-store__group-title');
        if (title) title.textContent = state;
        group.querySelector('.find-in-store__group-stores')?.append(...(rowsByState.get(state) ?? []));

        return group;
      });
  }

  /**
   * @param {string} name - A store's name as the stock data reports it.
   * @returns {HTMLTemplateElement | undefined} The template describing that store, or the plain one.
   */
  #findStoreTemplate(name) {
    /** @type {HTMLTemplateElement[]} */
    const templates = Array.from(this.querySelectorAll('template[data-store-name]'));

    return (
      templates.find((template) => template.dataset.storeName === name) ??
      templates.find((template) => template.dataset.storeName === '')
    );
  }

  /**
   * @param {HTMLTemplateElement} template - The store's row markup.
   * @param {string} name - The store's name.
   * @param {number} stockOnHand - Units of the picked size the store holds.
   * @returns {DocumentFragment} One store row.
   */
  #createStoreRow(template, name, stockOnHand) {
    const threshold = Number(this.dataset.lowStockThreshold) || 0;
    const isLow = stockOnHand <= threshold;
    const row = /** @type {DocumentFragment} */ (template.content.cloneNode(true));
    const nameElement = row.querySelector('.find-in-store__store-name');
    const labelElement = row.querySelector('.find-in-store__store-label');

    row.querySelector('.find-in-store__store')?.setAttribute('data-stock', isLow ? 'low' : 'in');
    if (nameElement) nameElement.textContent = name;
    if (labelElement) {
      labelElement.textContent = (isLow ? this.dataset.labelLowStock : this.dataset.labelInStock) ?? '';
    }

    return row;
  }
}

if (!customElements.get('find-in-store')) {
  customElements.define('find-in-store', FindInStore);
}

/**
 * @param {StockBySku} stock
 * @param {string | undefined} sku
 * @returns {[name: string, stockOnHand: number][]} The stores with at least one unit of the variant.
 */
function storesHolding(stock, sku) {
  return Array.from(stock.get(sku ?? '') ?? []).filter(([, stockOnHand]) => stockOnHand > 0);
}

/**
 * @param {string} url - The stock-in-store URL for a style.
 * @returns {Promise<StockBySku>}
 */
async function fetchStock(url) {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Stock in store request failed with status ${response.status}`);
  }

  /** @type {{ sku: string, locations: { location: string, soh: number }[] }[]} */
  const rows = await response.json();

  /** @type {StockBySku} */
  const stock = new Map();
  for (const row of rows) {
    const stores = stock.get(row.sku) ?? new Map();
    for (const { location, soh } of row.locations) {
      stores.set(location, (stores.get(location) ?? 0) + soh);
    }
    stock.set(row.sku, stores);
  }

  return stock;
}
